<template>
  <div class="modal-card">
    <header class="modal-card-head">
      <p class="modal-card-title">Custom FEN Position</p>
      <button
        class="delete"
        aria-label="close"
        @click="$emit('close')"
      ></button>
    </header>
    <section class="modal-card-body">
      <b-field label="FEN Position" label-position="on-border">
        <b-input
          v-model="customFen"
          placeholder="Enter a FEN position..."
          @input="handleFenInput"
        />
      </b-field>

      <div v-if="customFen && !isValidFen" class="fen-validation-error mt-2">
        <b-tag type="is-danger is-light">Invalid FEN format</b-tag>
      </div>

      <div class="fen-actions mt-3">
        <!-- Presets Select -->
        <b-field label="Presets" label-position="on-border">
          <b-select
            v-model="selectedPreset"
            placeholder="Select a preset"
            size="is-small"
            @input="handlePresetSelect"
          >
            <option value="" disabled selected>Select preset</option>
            <option
              v-for="(preset, index) in fenPresets"
              :key="index"
              :value="preset.fen"
            >
              {{ preset.name }}
            </option>
          </b-select>
        </b-field>

        <!-- History Select -->
        <b-field label="History" label-position="on-border" class="ml-2">
          <b-select
            v-model="selectedHistory"
            placeholder="Select from history"
            size="is-small"
            :disabled="!fenHistory.length"
            @input="handleHistorySelect"
          >
            <option value="" disabled selected>Select from history</option>
            <option
              v-for="(fen, index) in fenHistory"
              :key="index"
              :value="fen"
            >
              {{ index + 1 }}. {{ getFenShortDescription(fen) }}
            </option>
          </b-select>
        </b-field>

        <!-- Clear History Button -->
        <b-button
          class="ml-2"
          type="is-danger is-light"
          size="is-small"
          icon-left="trash"
          :disabled="!fenHistory.length"
          @click="clearFenHistory"
        >
          Clear History
        </b-button>
      </div>
    </section>
    <footer class="modal-card-foot">
      <b-button
        type="is-primary"
        @click="loadCustomFen"
        :disabled="!isValidFen"
      >
        Load Position
      </b-button>
      <b-button @click="$emit('close')">Cancel</b-button>
    </footer>
  </div>
</template>

<script>
import Chess from "chess.js";
import {
  CHESS_START_POSITION,
  CHESS_EMPTY_BOARD_FEN,
} from "vue-chess-shared/constants";

export default {
  name: "FenPositionEditor",

  data() {
    return {
      customFen: "",
      isValidFen: false,
      fenHistory: [],
      selectedPreset: "",
      selectedHistory: "",
      fenPresets: [
        {
          name: "Starting Position",
          fen: CHESS_START_POSITION,
        },
        {
          name: "Empty Board",
          fen: CHESS_EMPTY_BOARD_FEN,
        },
        {
          name: "Basic Mate - King and Queen vs King",
          fen: "8/8/8/3k4/8/8/4K3/7Q w - - 0 1",
        },
        {
          name: "Basic Mate - Two Rooks",
          fen: "8/8/8/3k4/8/8/R7/R3K3 w - - 0 1",
        },
        {
          name: "Rook Endgame",
          fen: "8/8/8/8/8/8/4k3/4K2R w K - 0 1",
        },
        {
          name: "Queen vs Pawns",
          fen: "8/8/8/8/8/k7/ppp5/1KQ5 w - - 0 1",
        },
        {
          name: "Knight Fork Position",
          fen: "5rk1/5ppp/8/3N4/8/8/8/6K1 w - - 0 1",
        },
        {
          name: "Bishop Pair vs Knight",
          fen: "8/8/3k4/8/2n5/4B3/3B4/3K4 w - - 0 1",
        },
        {
          name: "Pawn Endgame",
          fen: "8/8/1k6/8/1P6/8/8/1K6 w - - 0 1",
        },
        {
          name: "Opposite Colored Bishops",
          fen: "8/8/3k4/8/3B4/8/3K4/7b w - - 0 1",
        },
        {
          name: "Double Bishop Mate",
          fen: "7k/8/6K1/8/8/8/5B2/6B1 w - - 0 1",
        },
        {
          name: "King and Pawn vs King",
          fen: "8/8/8/3k4/8/3K4/3P4/8 w - - 0 1",
        },
        {
          name: "Rook vs Pawn",
          fen: "8/8/8/3k4/8/3p4/3K4/3R4 w - - 0 1",
        },
        {
          name: "Queen vs Rook",
          fen: "8/8/8/3k4/8/3r4/3K4/3Q4 w - - 0 1",
        },
        {
          name: "Lucena Position",
          fen: "3k4/3P4/3K4/8/8/8/8/4r3 w - - 0 1",
        },
      ],
      maxHistoryItems: 10,
    };
  },

  created() {
    // Load FEN history from localStorage if available
    const savedHistory = localStorage.getItem("fenHistory");
    if (savedHistory) {
      try {
        this.fenHistory = JSON.parse(savedHistory);
      } catch (e) {
        console.error("Failed to parse FEN history from localStorage", e);
        this.fenHistory = [];
      }
    }
  },

  methods: {
    // Method to validate FEN input
    handleFenInput(value) {
      try {
        // Use chess.js to validate the FEN
        const chess = new Chess();
        this.isValidFen = chess.load(value);
      } catch (error) {
        this.isValidFen = false;
      }
      if (value !== this.selectedHistory) {
        this.selectedHistory = "";
      }
      if (value !== this.selectedPreset) {
        this.selectedPreset = "";
      }
    },

    // Method to handle preset selection
    handlePresetSelect(fen) {
      if (fen) {
        this.selectedHistory = ""; // Clear history selection
        this.applyFenPreset(fen);
      }
    },

    // Method to handle history selection
    handleHistorySelect(fen) {
      if (fen) {
        this.selectedPreset = ""; // Clear preset selection
        this.applyFenPreset(fen);
      }
    },

    // Method to load custom FEN
    loadCustomFen() {
      if (!this.isValidFen) return;

      // Add to FEN history
      this.addToFenHistory(this.customFen);

      // Emit the event with the FEN and PGN
      this.$emit("fen-loaded", this.customFen);

      // Close the modal
      this.$emit("close");
    },

    // Method to apply a FEN preset
    applyFenPreset(fen) {
      this.customFen = fen;
      // Explicitly validate the FEN after setting it
      this.handleFenInput(fen);
    },

    // Method to add a FEN to history
    addToFenHistory(fen) {
      // Don't add if it's already the most recent entry
      if (this.fenHistory[0] === fen) return;

      // Remove if it exists elsewhere in the history
      const index = this.fenHistory.indexOf(fen);
      if (index !== -1) {
        this.fenHistory.splice(index, 1);
      }

      // Add to the beginning of the array
      this.fenHistory.unshift(fen);

      // Limit the history size
      if (this.fenHistory.length > this.maxHistoryItems) {
        this.fenHistory = this.fenHistory.slice(0, this.maxHistoryItems);
      }

      // Save to localStorage
      localStorage.setItem("fenHistory", JSON.stringify(this.fenHistory));

      // Reset both selects
      this.selectedPreset = "";
      this.selectedHistory = "";
    },

    // Method to clear FEN history
    clearFenHistory() {
      this.fenHistory = [];
      localStorage.removeItem("fenHistory");
      this.selectedHistory = "";
    },

    // Method to get a short description of a FEN
    getFenShortDescription(fen) {
      const pieceCounts = {};
      const fenParts = fen.split(" ");
      const position = fenParts[0];

      // Count pieces
      for (const char of position) {
        if (/[pnbrqkPNBRQK]/.test(char)) {
          pieceCounts[char] = (pieceCounts[char] || 0) + 1;
        }
      }

      // Create a short description
      const pieceList = Object.entries(pieceCounts)
        .map(([piece, count]) => `${count}${piece}`)
        .join(" ");

      return pieceList || "Empty board";
    },
  },
};
</script>

<style scoped>
.fen-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
}

@media screen and (max-width: 768px) {
  .fen-actions {
    flex-direction: column;
  }

  .fen-actions .ml-2 {
    margin-left: 0 !important;
    margin-top: 0.5rem;
  }
}
</style>
